import React, { useState, useEffect } from 'react';

/**
 * Utilitário para formatação de valores monetários
 */

export interface CurrencyConfig {
  code: string;
  symbol: string;
  position: 'before' | 'after';
}

// Function to get default configuration (prevents initialization issues)
function getDefaultConfig(): CurrencyConfig {
  return {
    code: 'USD',
    symbol: '$',
    position: 'before'
  };
}

// Configuração padrão exportada como função
export const defaultConfig = getDefaultConfig();

// Opções de formatação para o Intl.NumberFormat
export type  FormatOptions = {
  decimalPlaces?: number;
  showCurrency?: boolean;
  useSymbol?: boolean;
  locale?: string;
  style?: 'compact' | 'normal';
  size?: 'sm' | 'md' | 'lg';
  colorClass?: string;
};

/**
 * Formata um valor monetário de acordo com a configuração de moeda
 * 
 * @param amount - Valor a ser formatado
 * @param currency - Configuração da moeda (opcional, usa o padrão se não informado)
 * @param options - Opções adicionais de formatação
 * @returns String formatada
 */
export function formatCurrency(
  amount: number, 
  currency: CurrencyConfig = getDefaultConfig(),
  options: FormatOptions = {}
): string {
  // Definir opções padrão
  const decimalPlaces = options.decimalPlaces ?? 2;
  const showCurrency = options.showCurrency ?? false;
  const useSymbol = options.useSymbol ?? true;
  const locale = options.locale ?? 'en-US';
  const style = options.style ?? 'normal';

  // Configurar Intl.NumberFormat com base nas opções
  const numberFormatOptions: Intl.NumberFormatOptions = {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  };
  
  // Usar notação compacta para valores grandes
  if (style === 'compact') {
    numberFormatOptions.notation = 'compact';
    numberFormatOptions.compactDisplay = 'short';
  }

  // Formatar o valor numérico
  const formattedValue = new Intl.NumberFormat(locale, numberFormatOptions).format(amount);

  // Se não quiser mostrar símbolo ou código da moeda, retornar apenas o valor
  if (!showCurrency && !useSymbol) {
    return formattedValue;
  }

  // Determinar o que mostrar (símbolo ou código)
  const currencyIndicator = useSymbol ? currency.symbol : currency.code;
  
  // Posicionar conforme configuração
  if (currency.position === 'before') {
    return `${currencyIndicator}${formattedValue}`;
  } else {
    return `${formattedValue} ${currencyIndicator}`;
  }
}

/**
 * Hook simplificado para formatação de moeda
 * Use apenas quando tiver certeza de que o contexto está disponível
 */
export function useCurrencyFormatter() {
  // Usar configuração padrão por segurança
  const defaultConfig = getDefaultConfig();
  
  return (amount: number, options?: FormatOptions) => 
    formatCurrency(amount, defaultConfig, options);
}

/**
 * Componente para exibir um valor monetário formatado
 * Versão simplificada que evita problemas de hooks
 */
export function CurrencyDisplay({ 
  amount, 
  currency,
  options 
}: { 
  amount: number;
  currency?: CurrencyConfig;
  options?: FormatOptions;
}) {
  // Usar sempre a currency passada por prop ou o padrão
  // Se precisar do contexto, isso deve ser feito no componente pai
  const currencyConfig = currency || getDefaultConfig();
  
  try {
    // Determinar o tamanho e a classe de cor
    let className = '';
    if (options?.size) {
      switch(options.size) {
        case 'sm': className += 'text-sm '; break;
        case 'md': className += 'text-base '; break;
        case 'lg': className += 'text-lg font-bold '; break;
      }
    }
    
    if (options?.colorClass) {
      className += options.colorClass;
    }
    
    const formattedValue = formatCurrency(amount, currencyConfig, options);
    
    return className ? <span className={className}>{formattedValue}</span> : <span>{formattedValue}</span>;
  } catch (error) {
    // Fallback para quando há erro na formatação
    console.warn('Erro na formatação de moeda:', error);
    return <span>{formatCurrency(amount, getDefaultConfig(), options)}</span>;
  }
}
